import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, PageHeader, Spinner, StatusPill } from '../components/ui';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import { ComposeBroadcastDialog } from '../components/dialogs/ComposeBroadcastDialog';
import { UsersAPI } from '../services/endpoints/admin';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { Subscription, User } from '../types/api';
import { formatDate, formatDateTime, relativeTime } from '../utils/format';
import './MediaDetailPage.css';

export function UserDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<'suspend' | 'unsuspend' | 'refresh-cache' | null>(null);
  const [messaging, setMessaging] = useState(false);
  const { run } = useDispatchAction();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const u = await UsersAPI.get(id);
        if (alive) setUser(u);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  async function performAction(reason?: string) {
    if (!user || !acting) return;
    const kind = acting;
    const r = reason || 'Admin action';
    if (kind === 'refresh-cache') {
      await run('Refresh subscription cache', () => UsersAPI.refreshCache(user.id, r));
      setActing(null);
      return;
    }
    const result = await run(kind === 'suspend' ? 'Suspend user' : 'Restore user',
      () => kind === 'suspend' ? UsersAPI.suspend(user.id, r) : UsersAPI.unsuspend(user.id, r));
    setActing(null);
    if (result !== null) setUser(u => (u ? { ...u, disabled: kind === 'suspend' } : u));
  }

  if (loading) return <Card><Spinner /> Loading…</Card>;
  if (!user) return <Card>User not found.</Card>;

  const name = [user.firstname, user.lastname].filter(Boolean).join(' ');
  const recipientName = name || undefined;

  return (
    <div>
      <PageHeader
        eyebrow={`04 — USER · ${user.id.slice(0, 8)}`}
        title={<>{name || user.email}</>}
        lede={<>Status: <UserStatusPill user={user} /> &nbsp;·&nbsp; {user.email} &nbsp;·&nbsp; Joined {formatDate(user.createdAt)}</>}
        actions={
          <>
            <Button onClick={() => nav('/users')}>← Back</Button>
            <Button tone="ember" onClick={() => setMessaging(true)}>✉ Message</Button>
            {user.disabled
              ? <Button tone="ghost" onClick={() => setActing('unsuspend')}>Restore</Button>
              : <Button tone="danger" onClick={() => setActing('suspend')}>Suspend</Button>}
          </>
        }
      />

      <div className="md-grid">
        <div className="md-col">
          <Card>
            <h2 className="md-h">Identity</h2>
            <dl className="md-static">
              <Row label="Email" value={user.email} />
              <Row label="First name" value={user.firstname || '—'} />
              <Row label="Last name" value={user.lastname || '—'} />
              <Row label="Roles" value={user.roles?.length ? user.roles.join(' · ') : '—'} />
              <Row label="Status" value={<UserStatusPill user={user} />} />
            </dl>
          </Card>

          <Card>
            <div className="md-section-head">
              <h2 className="md-h">Subscription</h2>
              <Button size="sm" tone="ghost" onClick={() => setActing('refresh-cache')}>↻ Refresh cache</Button>
            </div>
            <SubscriptionBlock subscription={user.subscription} />
          </Card>
        </div>

        <aside className="md-rail">
          <Card>
            <h3 className="md-h-mono">ID &amp; activity</h3>
            <dl className="md-static md-static--rail">
              <Row label="ID"        value={<code className="md-mono">{user.id}</code>} />
              <Row label="Joined"    value={formatDateTime(user.createdAt)} />
              <Row label="Last seen" value={relativeTime(user.lastSeen)} />
            </dl>
          </Card>
        </aside>
      </div>

      <ComposeBroadcastDialog
        open={messaging}
        onClose={() => setMessaging(false)}
        onSent={() => setMessaging(false)}
        lockedRecipient={{ email: user.email, name: recipientName }}
      />

      <ConfirmDialog
        open={!!acting}
        title={acting === 'suspend' ? 'Suspend account'
          : acting === 'refresh-cache' ? 'Refresh subscription cache' : 'Restore account'}
        message={acting === 'suspend'
          ? `Suspending ${user.email} blocks sign-in immediately. They will see a generic "account unavailable" message.`
          : acting === 'refresh-cache'
            ? `Drops cached account and subscription data for ${user.email}. Their next playback reloads it from the database. They stay signed in.`
            : `Restoring ${user.email} re-enables sign-in.`}
        requireReason
        destructive={acting === 'suspend'}
        confirmLabel={acting === 'suspend' ? 'Suspend' : acting === 'refresh-cache' ? 'Refresh' : 'Restore'}
        onClose={() => setActing(null)}
        onConfirm={performAction}
      />
    </div>
  );
}

function SubscriptionBlock({ subscription }: { subscription?: Subscription }) {
  if (!subscription) {
    return <p className="md-section-note">No subscription on record — this is a free account.</p>;
  }
  const s = subscription;
  return (
    <dl className="md-static">
      <Row label="Plan" value={s.plan || '—'} />
      <Row label="Status" value={<SubStatusPill status={s.status} />} />
      <Row label="Channel" value={s.channel || '—'} />
      <Row label="Expires" value={formatDate(s.expiresAt)} />
      <Row label="Auto-renew" value={s.autoRenew == null ? '—' : s.autoRenew ? 'On' : 'Off'} />
      {!!s.failureCount && <Row label="Failures" value={String(s.failureCount)} />}
    </dl>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="md-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function UserStatusPill({ user }: { user: User }) {
  if (user.disabled) return <StatusPill tone="rust">Suspended</StatusPill>;
  if (user.subscription?.status === 'ACTIVE') return <StatusPill tone="moss">Active</StatusPill>;
  return <StatusPill tone="brass">Free</StatusPill>;
}

function SubStatusPill({ status }: { status: Subscription['status'] }) {
  if (status === 'ACTIVE')  return <StatusPill tone="moss">Active</StatusPill>;
  if (status === 'GIFTED')  return <StatusPill tone="moss">Gifted</StatusPill>;
  if (status === 'EXPIRED') return <StatusPill tone="brass">Expired</StatusPill>;
  if (status === 'FAILED')  return <StatusPill tone="rust">Failed</StatusPill>;
  if (status === 'CANCELLED') return <StatusPill tone="rust">Cancelled</StatusPill>;
  return <StatusPill tone="brass">{status}</StatusPill>;
}
